import type { AttentionLevel } from '../../db/models'
import { endsWord, startsWord } from '../boundaries'
import type { CatalogProject } from '../types'
import type { Candidate, Recognizer } from './types'

/** Positionen eines Zeichens, vor dem ein neues Wort beginnt (`#`, `/`, `@`). */
function markers(text: string, char: string): number[] {
  const found: number[] = []
  for (let i = text.indexOf(char); i !== -1; i = text.indexOf(char, i + 1)) {
    if (startsWord(text, i)) found.push(i)
  }
  return found
}

/** Der längste Name, mit dem der Text hinter `from` beginnt und dort als Wort endet. */
function longestNameAt<T>(text: string, from: number, items: T[], nameOf: (item: T) => string): T[] {
  const rest = text.slice(from).toLowerCase()
  let bestLength = 0
  let best: T[] = []
  for (const item of items) {
    const name = nameOf(item).toLowerCase()
    if (!name || name.length < bestLength || !rest.startsWith(name) || !endsWord(text, from + name.length)) continue
    if (name.length > bestLength) {
      bestLength = name.length
      best = [item]
    } else {
      best.push(item)
    }
  }
  return best
}

const isActive = (project: CatalogProject) => project.status === undefined || project.status === 'active'

/**
 * **`#Projekt`** — der längste passende Name eines aktiven Projekts, auch mit Leerzeichen. Bei
 * gleichem Namen gewinnt, was im Vorschlag gewählt wurde (`chosen`), dann das Projekt der Seite (wer auf
 * „Test“ steht, meint dieses „Test“) bzw. in der
 * Schnelleingabe das im Hauptfenster offene, dann der aktuelle Bereich, sonst die Reihenfolge des
 * Katalogs (die der Sidebar).
 * Ein unbekanntes `#Wort` bleibt Text: Projekte entstehen nicht aus der Eingabe.
 */
export const recognizeProject: Recognizer = ({ text, options }) => {
  const projects = (options.catalog?.projects ?? []).filter(isActive)
  return markers(text, '#').flatMap((at): Candidate[] => {
    const matches = longestNameAt(text, at + 1, projects, p => p.name)
    const chosen = options.chosen?.find(c => c.kind === 'project' && c.start === at)?.id
    const project = matches.find(p => p.id === chosen)
      ?? matches.find(p => p.id === options.contextProjectId)
      ?? matches.find(p => p.id === options.preferredProjectId)
      ?? matches.find(p => p.lifeSpace === options.currentLifeSpace)
      ?? matches[0]
    if (!project) return []
    const end = at + 1 + project.name.length
    return [{ kind: 'project', start: at, end, text: text.slice(at, end), value: { id: project.id, lifeSpace: project.lifeSpace } }]
  })
}

/**
 * **`/Abschnitt`** — ein Abschnitt des Projekts davor (`#Steuer /Belege`), sonst des Projekts der
 * Seite. Ohne beides bleibt `/Wort` Text (Kurz notiert, Demnächst, Schnelleingabe).
 */
export const recognizeSection: Recognizer = ({ text, options, accepted }) => {
  const projectId = accepted.find(c => c.kind === 'project')?.value.id ?? options.contextProjectId
  if (!projectId) return []
  const sections = (options.catalog?.sections ?? []).filter(s => s.projectId === projectId)
  return markers(text, '/').flatMap((at): Candidate[] => {
    const matches = longestNameAt(text, at + 1, sections, s => s.title)
    const chosen = options.chosen?.find(c => c.kind === 'section' && c.start === at)?.id
    const section = matches.find(s => s.id === chosen) ?? matches[0]
    if (!section) return []
    const end = at + 1 + section.title.length
    return [{ kind: 'section', start: at, end, text: text.slice(at, end), value: { id: section.id } }]
  })
}

/**
 * **`@Tag`** — ein vorhandener Tag ohne Rücksicht auf Groß/klein, auch mit Leerzeichen; sonst ein
 * neues Wort aus Buchstaben, Ziffern, `-` und `_`, das beim Speichern angelegt wird. `@` nur am
 * Wortanfang, damit „max@example.com“ kein Tag wird.
 */
export const recognizeTag: Recognizer = ({ text, options }) => {
  const tags = options.catalog?.tags ?? []
  return markers(text, '@').flatMap((at): Candidate[] => {
    const known = longestNameAt(text, at + 1, tags, t => t.name)[0]
    if (known) {
      const end = at + 1 + known.name.length
      return [{ kind: 'tag', start: at, end, text: text.slice(at, end), value: { id: known.id, name: known.name } }]
    }
    const name = /^[\p{L}\p{N}_-]+/u.exec(text.slice(at + 1))?.[0]
    if (!name || !endsWord(text, at + 1 + name.length)) return []
    const end = at + 1 + name.length
    return [{ kind: 'tag', start: at, end, text: text.slice(at, end), value: { name } }]
  })
}

const ATTENTION: Record<string, AttentionLevel> = { '1': 'high', '2': 'medium', '3': 'low' }

/** **`p1`–`p3`** wie bei Todoist und im Import: p1 = „Als erstes“ … p3 = „Wenn möglich“. `p4` bleibt Text. */
export const recognizeAttention: Recognizer = ({ text }) => {
  const found: Candidate[] = []
  for (const match of text.matchAll(/p([1-3])/giu)) {
    const start = match.index!
    const end = start + match[0].length
    if (!startsWord(text, start) || !endsWord(text, end)) continue
    found.push({ kind: 'attention', start, end, text: match[0], value: ATTENTION[match[1]!]! })
  }
  return found
}
